import { MBSettingsRequest } from '@business/entities/marketing';
import { NylasRequest } from '@business/entities/nylas';
import {
  SettingsResponse,
  QBSubCompare,
  OrderSettingResponse,
  DealSettingResponse,
  TMSettingResponse,
  AutoTagsSettingResponse,
} from '@business/entities/settings';
import { CustomerDashboardSettings } from '@business/entities/settings/CustomerDashboardSettings';
import { ClientDashboardSettings } from '@business/entities/settings/ClientDashboardSettings';
import { EscalateRequest } from '@business/entities/tenant';
import { Endpoint } from '../endpoint';
import { memoize } from '@core/utils/rxjs/operators';
import { DealSettingsFormData } from '@modules/settings/SettingsPage/Components/DealsTabs/Tabs/DealSettingsTab';
import { CommissionConfigFormData } from '@modules/settings/SettingsPage/Components/CommissionSettingsTab/CommissionConfigTab';
import { ProposalSettingsFormData } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/ProposalSettingsTab';
import { TMProjectSettingsFormData } from '@modules/settings/SettingsPage/Components/ProjectsTabs/Tabs/ProjectSettingsTab/ProjectSettingsTab';
import { SettingsFormData } from '@modules/settings/SettingsPage/Components/StatusTab/StatusSettingsTab/StatusSettingsTab';
import { ClockinUsersSettingsFormData } from '@modules/settings/SettingsPage/Components/ClockinTab/ClockinUserSettingsTab';
import { TMSettingsFormData } from '@modules/settings/SettingsPage/Components/TimeMaterialsTab/Tabs/FlagStatusTab/FlagStatusTab';

export function fetchSettings(fromMemoize = false) {
  return Endpoint.get<SettingsResponse>(`@gc/settings`).pipe(
    memoize({
      key: 'fetchSettings',
      options: { timeout: 60, refresh: !fromMemoize },
    }),
  );
}

export function updateNylasConfig(data: NylasRequest) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/nylas`, { data });
}

export function updateEscalate(data: EscalateRequest) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/escalate`, { data });
}

export function updateServiceClassSetting(value: boolean) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/service-class`, { data: { value } });
}

export function updateMBSettings(data: MBSettingsRequest) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/mb`, { data });
}

export function updateOrderSettings(data: Partial<OrderSettingResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/order`, { data });
}

export function updateDealSettings(data: DealSettingsFormData) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/deal`, { data });
}

export function updateTMSettings(data: TMSettingsFormData) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/tm-flags`, { data });
}

export function updateBillingSettings(data: Partial<SettingsResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/billing`, { data });
}

export function updateProposalSettings(data: ProposalSettingsFormData) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/proposal`, { data });
}

export function updateCustomerDashboardSettings(data: CustomerDashboardSettings) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/customer-dashboard`, { data });
}

export function updateClientDashboardSettings(data: ClientDashboardSettings) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/client-dashboard`, { data });
}

export function getQBSubCompare() {
  return Endpoint.get<QBSubCompare>(`@gc/settings/sub-compare`);
}

export function saveCommissionSettings(data: CommissionConfigFormData) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/commission`, { data });
}

export function updateTmSettings(data: Partial<TMSettingResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/tm`, { data });
}

export function updateUsersSettings(data: Partial<SettingsResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/clockin`, { data });
}

export function updateClockinUsersSettings(data: ClockinUsersSettingsFormData) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/clockin-user`, { data });
}

export function updateInstructorsSettings(data: Partial<SettingsResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/instructors`, { data });
}

export function updateProjectSettings(data: Partial<TMProjectSettingsFormData>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/projects`, { data });
}

export function updateSupportSettings(data: Partial<TMProjectSettingsFormData>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/support`, { data });
}

export function updateTagsSettings(data: Partial<AutoTagsSettingResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/tags`, { data });
}

export function updateStatusSettings(data: Partial<SettingsFormData>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/org-status`, { data });
}

export function updateAffilate(data: Partial<SettingsResponse>) {
  return Endpoint.post<SettingsResponse>(`@gc/settings/affiliate`, { data });
}
